import { Component, inject, OnInit, signal, computed, isDevMode } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LayoutService, ThemeMode } from '../layout.service';
import { AuthService, CompanyItem, CycleItem } from '../../services/auth.service';
import { NOMBRE_NIVEL } from '../../services/permissions.service';
import { MenuItem, MenuSection } from '../sidebar/menu-data';
import { MenuAccessService } from '../../core/permissions/menu-access.service';
import { SupportWidgetComponent } from '../../shared/support/support-widget.component';
import { CambiarContrasenaDialogComponent } from '../../shared/cambiar-contrasena/cambiar-contrasena-dialog.component';

export interface FlatRoute {
  label: string;
  route: string;
  parentLabel?: string;
  icon?: string;
  enConstruccion?: boolean;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, SupportWidgetComponent, CambiarContrasenaDialogComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  layoutService = inject(LayoutService);
  authService   = inject(AuthService);
  router        = inject(Router);
  private menuAccess = inject(MenuAccessService);

  companies        = signal<CompanyItem[]>([]);
  activeCycle      = signal<CycleItem | null>(null);
  switchingCompany = signal(false);
  isDev            = isDevMode();

  currentUser = computed(() => {
    const decoded = this.authService.decodeToken();
    if (!decoded) {
      return {
        username: 'Invitado',
        email: '',
        role: 'Invitado',
        avatarUrl: null as string | null
      };
    }
    return {
      username: decoded.username || (decoded.email ? decoded.email.split('@')[0] : 'Usuario'),
      email: decoded.email,
      // Encendidos los niveles, la etiqueta es el nivel de la cuenta; apagados,
      // el rol de siempre (folio 32D494).
      role: decoded.levelsEnabled ? (NOMBRE_NIVEL[decoded.level ?? 4] ?? 'Usuario') : (decoded.roles?.[0] || 'Usuario'),
      avatarUrl: null as string | null
    };
  });

  // Search functionality
  searchTerm = signal('');
  /** Sólo las pantallas que el menú le muestra al usuario: lo que no ve, tampoco lo encuentra. */
  flatRoutes = computed(() => this.flattenMenu(this.menuAccess.sections()));
  
  /** Quita acentos/diacríticos para que "division" encuentre "División". */
  private normalize(value: string): string {
    return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  }

  searchResults = computed(() => {
    const term = this.normalize(this.searchTerm().trim());
    if (!term) return [];

    return this.flatRoutes().filter(route =>
      this.normalize(route.label).includes(term) ||
      (route.parentLabel && this.normalize(route.parentLabel).includes(term))
    ).slice(0, 8); // Limit to 8 results
  });

  ngOnInit(): void {
    // El ciclo vigente acompana a la razon social en el panel superior.
    this.authService.getCurrentCycle().subscribe(cycle => this.activeCycle.set(cycle));
    this.authService.getMyCompanies().subscribe({
      next: list => {
        this.companies.set(list);
        // Marca la empresa activa según el token actual
        const currentId = this.authService.getCurrentBusinessNameId();
        const current   = list.find(c => c.businessNameId === currentId) ?? null;
        this.authService.activeCompany.set(current);
        // La sesion trae una razon social que no esta en su lista (se desactivo, le
        // quitaron el acceso o la sesion viene de otra base): se cambia de verdad a la
        // primera. Antes solo se mostraba y las pantallas seguian trabajando con la otra.
        if (!current && list.length) this.selectCompany(list[0]);
      },
      error: () => { /* silencioso — el token puede no estar listo */ },
    });
  }

  selectCompany(company: CompanyItem): void {
    if (company.businessNameId === this.authService.activeCompany()?.businessNameId) return;
    this.switchingCompany.set(true);
    this.authService.switchCompany(company.businessNameId).subscribe({
      next: () => {
        this.authService.activeCompany.set(company);
        this.switchingCompany.set(false);
        // Recarga la página actual para que todos los servicios usen el nuevo token
        window.location.reload();
      },
      error: () => this.switchingCompany.set(false),
    });
  }

  toggleMenu() { this.layoutService.toggleMenu(); }

  setTheme(mode: ThemeMode) { this.layoutService.setTheme(mode); }

  get themeIcon(): string {
    switch (this.layoutService.themeMode()) {
      case 'light':  return 'bx bx-sun';
      case 'dark':   return 'bx bx-moon';
      case 'system': return 'bx bx-desktop';
    }
  }

  get themeLabel(): string {
    switch (this.layoutService.themeMode()) {
      case 'light':  return 'Claro';
      case 'dark':   return 'Oscuro';
      case 'system': return 'Sistema';
    }
  }

  private flattenMenu(sections: MenuSection[]): FlatRoute[] {
    const flat: FlatRoute[] = [];
    
    const extractRoutes = (items: MenuItem[], parentLabel?: string, icon?: string) => {
      items.forEach(item => {
        if (item.route) {
          flat.push({
            label: item.label,
            route: item.route,
            parentLabel,
            icon,
            enConstruccion: item.enConstruccion
          });
        }
        if (item.children) {
          extractRoutes(item.children, item.label, icon);
        }
      });
    };

    sections.forEach(section => {
      extractRoutes(section.children, section.label, section.icon);
    });

    return flat;
  }

  navigateTo(item: FlatRoute): void {
    this.searchTerm.set('');
    if (item.enConstruccion && !this.isDev) {
      this.router.navigate(['/en-construccion'], { queryParams: { from: item.route } });
    } else {
      this.router.navigateByUrl(item.route);
    }
  }

  // Cambio voluntario de la contraseña propia (folio 32D494). La cuenta master está
  // blindada: su contraseña no se cambia desde el sistema, así que no ve la opción.
  esCuentaMaster = computed(() =>
    (this.authService.decodeToken()?.username ?? '').trim().toLowerCase() === 'master');
  showCambio   = signal(false);
  cambioCuenta = '';

  abrirCambioContrasena(): void {
    const decoded = this.authService.decodeToken();
    this.cambioCuenta = decoded?.username || decoded?.email || '';
    this.showCambio.set(true);
  }

  // Aviso previo a la caducidad de la contrasena (folio 32D494): llega con el inicio de
  // sesion y se queda hasta que la persona lo cierra o cambia la contrasena.
  avisoCaducidad = signal<number | null>(this.authService.getPasswordExpiryNotice());
  textoCaducidad = computed(() => {
    const n = this.avisoCaducidad();
    if (n === null) return '';
    if (n <= 0) return 'Tu contraseña vence hoy.';
    if (n === 1) return 'Tu contraseña vence mañana.';
    return `Tu contraseña vence en ${n} días.`;
  });

  descartarAvisoCaducidad(): void {
    this.authService.clearPasswordExpiryNotice();
    this.avisoCaducidad.set(null);
  }

  /**
   * Al cambiar la contraseña el API cierra las sesiones de la cuenta: se limpia el navegador
   * y se vuelve al inicio de sesión con el aviso de que ya quedó.
   */
  onContrasenaCambiada(): void {
    this.authService.logout(false);
    this.router.navigate(['/login'], { queryParams: { cambio: 1 } });
  }

  lockSession(): void {
    const decoded = this.authService.decodeToken();
    if (decoded) {
      const lockedUser = {
        email: decoded.email,
        username: decoded.username || (decoded.email ? decoded.email.split('@')[0] : 'Usuario')
      };
      localStorage.setItem('locked_user', JSON.stringify(lockedUser));
    }
    this.authService.logout(false);
    this.router.navigate(['/lockscreen'], { queryParams: { returnUrl: this.router.routerState.snapshot.url } });
  }
}
